import { Stack, Typography, Box } from '@mui/material';
import { ContractPageData, GetContractBillingScheduleResponse } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import { Period } from '@vy/types';
import { isNotNullOrUndefined, values, vyDate } from '@vy/utils';
import { sortBy } from 'lodash';
import { useMemo, useState } from 'react';
import { BaseSelect } from '../../../../../components/design/selects/BaseSelect';
import { ContractPhasesTimelineContainer } from '../ContractPhasesSection/ContractPhasesTimeline/ContractPhasesTimelineContainer';
import { ContractProductsTimelineRowBar } from '../ContractPhasesSection/ContractPhasesTimeline/ContractProductsTimelineRowBar';
import { buildProductPeriodsData } from '../ContractPhasesSection/contract-phases-section-utils/product-periods-builder';
import { BillingScheduleChart } from './ContractBillingScheduleChart';
import {
  buildBillingScheduleDataForInvoices,
  buildChartData,
  buildCompleteTimeline,
  buildProductRevenueData,
} from './contract-billing-schedule-utils';

type ContractBillingScheduleChartSectionProps = {
  billingScheduleData: GetContractBillingScheduleResponse['invoices'];
  contract: ContractPageData['contract'];
  startDate?: Date;
  endDate?: Date;
};

const DROPDOWN_OPTIONS = {
  Products: 'Product breakdown',
  Invoices: 'Invoices',
} as const;

export const ContractBillingScheduleChartSection: React.FC<ContractBillingScheduleChartSectionProps> = ({
  billingScheduleData,
  contract,
  startDate,
  endDate,
}) => {
  const [scheduleByInvoice, setScheduleByInvoice] = useState(true);

  const dataSource = useMemo<GetContractBillingScheduleResponse['invoices']>(() => {
    if (scheduleByInvoice) {
      return buildBillingScheduleDataForInvoices(billingScheduleData);
    }

    return billingScheduleData;
  }, [scheduleByInvoice, billingScheduleData]);

  const chartData = useMemo(() => {
    if (!startDate || !endDate) {
      return sortBy(buildChartData(dataSource), (item) => vyDate(item.monthYear).toDate());
    }

    const timeline = buildCompleteTimeline(startDate, endDate);
    const existingData = buildChartData(dataSource);
    const existingDataMap = new Map(existingData.map((item) => [item.monthYear, item]));

    return timeline.map((monthYear) => {
      const existingItem = existingDataMap.get(monthYear);
      return existingItem || { monthYear };
    });
  }, [dataSource, startDate, endDate]);

  const revenueData = useMemo(() => buildProductRevenueData(dataSource), [dataSource]);

  const contractLength: Period = {
    startTime: contract.startDate,
    endTime: buildContractEndDate(contract.endDate, contract.startDate),
  };

  const timelineChartData = useMemo(() => {
    return buildProductPeriodsData(contractLength, contract.plan.products);
  }, [dataSource]);

  return (
    <Box sx={{ overflowX: 'auto', width: '100%' }}>
      <div className="mx-2">
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: !scheduleByInvoice ? 2 : 0
          }}>
          <Stack
            direction="column"
            sx={{
              width: '250px',
              gap: 1,
              justifyContent: 'end',
              alignItems: 'center'
            }}>
            <BaseSelect
              fullWidth
              topLabel="Show graph by"
              defaultValue={DROPDOWN_OPTIONS.Invoices}
              withNone={false}
              values={values(DROPDOWN_OPTIONS)}
              onChange={(e) => {
                const value = e.target.value as keyof typeof DROPDOWN_OPTIONS;
                setScheduleByInvoice(value === DROPDOWN_OPTIONS.Invoices);
              }}
            />
          </Stack>
          <Stack
            direction="row"
            sx={{
              gap: 2,
              justifyContent: 'end',
              alignItems: 'center',
              paddingRight: 4
            }}>
            <Stack
              direction="row"
              sx={{
                gap: 1,
                alignItems: 'center'
              }}>
              <Typography>{scheduleByInvoice ? 'Invoiced' : 'Fixed'}</Typography>
              <VyIcon icon={'RectangleHollow'} />
            </Stack>
            <Stack
              direction="row"
              sx={{
                gap: 1,
                alignItems: 'center'
              }}>
              <Typography>{scheduleByInvoice ? 'Uninvoiced' : 'Usage based'}</Typography>
              <VyIcon icon={'RectangleLined'} />
            </Stack>
          </Stack>
        </Stack>

        {scheduleByInvoice ? (
          <BillingScheduleChart chartData={chartData} revenueData={revenueData} />
        ) : (
          <ContractPhasesTimelineContainer
            contractLength={contractLength}
            chartData={timelineChartData}
            BarComponent={ContractProductsTimelineRowBar}
          />
        )}
      </div>
    </Box>
  );
};

function buildContractEndDate(endDate: Date | undefined, startDate: Date): Date {
  const defaultEndDate = vyDate(startDate).add(1, 'year').toDate();

  return isNotNullOrUndefined(endDate) ? endDate : defaultEndDate;
}
